import React, { useState } from 'react';
import { Search, Filter, X, SlidersHorizontal, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Separator } from '@/components/ui/separator';
import { Checkbox } from '@/components/ui/checkbox';
import { Slider } from '@/components/ui/slider';
import { formatPrice } from '@/lib/utils';

export interface SearchFilters {
  query: string;
  status: string[];
  amountRange: [number, number];
  dateRange: {
    start?: string;
    end?: string;
  };
  restaurants: string[];
  categories: string[];
  sortBy: string;
  sortOrder: 'asc' | 'desc';
}

interface AdvancedSearchProps {
  filters: SearchFilters;
  onChange: (filters: SearchFilters) => void;
  availableStatuses?: string[];
  availableRestaurants?: string[];
  availableCategories?: string[];
  maxAmount?: number;
  className?: string;
}

export function AdvancedSearch({
  filters,
  onChange,
  availableStatuses = ['pending', 'confirmed', 'preparing', 'ready', 'delivered', 'cancelled'],
  availableRestaurants = [],
  availableCategories = [],
  maxAmount = 10000,
  className
}: AdvancedSearchProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [tempFilters, setTempFilters] = useState<SearchFilters>(filters);

  const updateTempFilter = (key: keyof SearchFilters, value: any) => {
    setTempFilters(prev => ({ ...prev, [key]: value }));
  };

  const applyFilters = () => {
    onChange(tempFilters);
    setIsOpen(false);
  };

  const resetFilters = () => {
    const defaultFilters: SearchFilters = {
      query: '',
      status: [],
      amountRange: [0, maxAmount],
      dateRange: {},
      restaurants: [],
      categories: [],
      sortBy: 'createdAt',
      sortOrder: 'desc'
    };
    setTempFilters(defaultFilters);
    onChange(defaultFilters);
    setIsOpen(false);
  };

  const hasActiveFilters = () => {
    return filters.status.length > 0 ||
           filters.restaurants.length > 0 ||
           filters.categories.length > 0 ||
           filters.amountRange[0] > 0 ||
           filters.amountRange[1] < maxAmount ||
           filters.dateRange.start ||
           filters.dateRange.end;
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (filters.status.length > 0) count++;
    if (filters.restaurants.length > 0) count++;
    if (filters.categories.length > 0) count++;
    if (filters.amountRange[0] > 0 || filters.amountRange[1] < maxAmount) count++;
    if (filters.dateRange.start || filters.dateRange.end) count++;
    return count;
  };

  const handleStatusToggle = (status: string) => {
    const newStatuses = tempFilters.status.includes(status)
      ? tempFilters.status.filter(s => s !== status)
      : [...tempFilters.status, status];
    updateTempFilter('status', newStatuses);
  };

  const handleRestaurantToggle = (restaurant: string) => {
    const newRestaurants = tempFilters.restaurants.includes(restaurant)
      ? tempFilters.restaurants.filter(r => r !== restaurant)
      : [...tempFilters.restaurants, restaurant];
    updateTempFilter('restaurants', newRestaurants);
  };

  const handleCategoryToggle = (category: string) => {
    const newCategories = tempFilters.categories.includes(category)
      ? tempFilters.categories.filter(c => c !== category)
      : [...tempFilters.categories, category];
    updateTempFilter('categories', newCategories);
  };

  const sortOptions = [
    { value: 'createdAt', label: 'Date Created' },
    { value: 'deliveryDate', label: 'Delivery Date' },
    { value: 'totalAmount', label: 'Order Value' },
    { value: 'restaurantName', label: 'Restaurant Name' },
    { value: 'status', label: 'Status' }
  ];

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Search Input */}
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <Input
            placeholder="Search by order number, restaurant, contact person, or email..."
            value={filters.query}
            onChange={(e) => onChange({ ...filters, query: e.target.value })}
            className="pl-10"
          />
        </div>
        <Popover open={isOpen} onOpenChange={setIsOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" className="relative">
              <SlidersHorizontal className="w-4 h-4 mr-2" />
              Advanced Filters
              {hasActiveFilters() && (
                <Badge variant="secondary" className="ml-2 h-5 px-1.5">
                  {getActiveFilterCount()}
                </Badge>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-96 p-0" align="end">
            <Card className="border-0 shadow-none">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <Filter className="h-5 w-5" />
                    Advanced Filters
                  </span>
                  {hasActiveFilters() && (
                    <Button variant="ghost" size="sm" onClick={resetFilters}>
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Order Status */}
                <div>
                  <Label className="text-sm font-medium mb-3 block">Order Status</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {availableStatuses.map((status) => (
                      <div key={status} className="flex items-center space-x-2">
                        <Checkbox
                          id={`status-${status}`}
                          checked={tempFilters.status.includes(status)}
                          onCheckedChange={() => handleStatusToggle(status)}
                        />
                        <Label 
                          htmlFor={`status-${status}`} 
                          className="text-sm capitalize cursor-pointer"
                        >
                          {status}
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>

                <Separator />

                {/* Order Amount Range */}
                <div>
                  <Label className="text-sm font-medium mb-3 block">Order Amount Range</Label>
                  <div className="space-y-4">
                    <Slider
                      value={tempFilters.amountRange}
                      onValueChange={(value) => updateTempFilter('amountRange', value as [number, number])}
                      max={maxAmount}
                      min={0}
                      step={50}
                      className="w-full"
                    />
                    <div className="flex items-center justify-between text-sm text-gray-600">
                      <span>{formatPrice(tempFilters.amountRange[0])}</span>
                      <span>{formatPrice(tempFilters.amountRange[1])}</span>
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Date Range */}
                <div>
                  <Label className="text-sm font-medium mb-3 block">Date Range</Label>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs text-gray-600">From</Label>
                      <Input
                        type="date"
                        value={tempFilters.dateRange.start || ''}
                        onChange={(e) => updateTempFilter('dateRange', {
                          ...tempFilters.dateRange,
                          start: e.target.value
                        })}
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-gray-600">To</Label>
                      <Input
                        type="date"
                        value={tempFilters.dateRange.end || ''}
                        onChange={(e) => updateTempFilter('dateRange', {
                          ...tempFilters.dateRange,
                          end: e.target.value
                        })}
                      />
                    </div>
                  </div>
                </div>

                {/* Restaurants */}
                {availableRestaurants.length > 0 && (
                  <>
                    <Separator />
                    <div>
                      <Label className="text-sm font-medium mb-3 block">
                        Restaurants ({availableRestaurants.length})
                      </Label>
                      <div className="max-h-32 overflow-y-auto space-y-2">
                        {availableRestaurants.slice(0, 10).map((restaurant) => (
                          <div key={restaurant} className="flex items-center space-x-2">
                            <Checkbox
                              id={`restaurant-${restaurant}`}
                              checked={tempFilters.restaurants.includes(restaurant)}
                              onCheckedChange={() => handleRestaurantToggle(restaurant)}
                            />
                            <Label 
                              htmlFor={`restaurant-${restaurant}`} 
                              className="text-sm cursor-pointer truncate"
                            >
                              {restaurant}
                            </Label>
                          </div>
                        ))}
                        {availableRestaurants.length > 10 && (
                          <p className="text-xs text-gray-500">
                            +{availableRestaurants.length - 10} more restaurants...
                          </p>
                        )}
                      </div>
                    </div>
                  </>
                )}

                {/* Categories */}
                {availableCategories.length > 0 && (
                  <>
                    <Separator />
                    <div>
                      <Label className="text-sm font-medium mb-3 block">Categories</Label>
                      <div className="grid grid-cols-2 gap-2">
                        {availableCategories.map((category) => (
                          <div key={category} className="flex items-center space-x-2">
                            <Checkbox
                              id={`category-${category}`}
                              checked={tempFilters.categories.includes(category)}
                              onCheckedChange={() => handleCategoryToggle(category)}
                            />
                            <Label 
                              htmlFor={`category-${category}`} 
                              className="text-sm cursor-pointer"
                            >
                              {category}
                            </Label>
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                )}

                <Separator />

                {/* Sort Options */}
                <div>
                  <Label className="text-sm font-medium mb-3 block">Sort By</Label>
                  <div className="grid grid-cols-2 gap-2">
                    <Select 
                      value={tempFilters.sortBy} 
                      onValueChange={(value) => updateTempFilter('sortBy', value)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {sortOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select 
                      value={tempFilters.sortOrder} 
                      onValueChange={(value) => updateTempFilter('sortOrder', value as 'asc' | 'desc')}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="desc">Newest First</SelectItem>
                        <SelectItem value="asc">Oldest First</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-2 pt-2">
                  <Button onClick={applyFilters} className="flex-1">
                    Apply Filters
                  </Button>
                  <Button variant="outline" onClick={() => setIsOpen(false)}>
                    Cancel
                  </Button>
                </div>
              </CardContent>
            </Card>
          </PopoverContent>
        </Popover>
      </div>

      {/* Active Filters Display */}
      {hasActiveFilters() && (
        <div className="flex flex-wrap gap-2">
          {filters.status.map((status) => (
            <Badge key={status} variant="secondary" className="gap-1">
              Status: {status}
              <X 
                className="w-3 h-3 cursor-pointer" 
                onClick={() => {
                  const newFilters = {
                    ...filters,
                    status: filters.status.filter(s => s !== status)
                  };
                  onChange(newFilters);
                }}
              />
            </Badge>
          ))}
          {filters.restaurants.map((restaurant) => (
            <Badge key={restaurant} variant="secondary" className="gap-1">
              {restaurant}
              <X 
                className="w-3 h-3 cursor-pointer" 
                onClick={() => {
                  const newFilters = {
                    ...filters,
                    restaurants: filters.restaurants.filter(r => r !== restaurant)
                  };
                  onChange(newFilters);
                }}
              />
            </Badge>
          ))}
          {(filters.amountRange[0] > 0 || filters.amountRange[1] < maxAmount) && (
            <Badge variant="secondary" className="gap-1">
              Amount: {formatPrice(filters.amountRange[0])} - {formatPrice(filters.amountRange[1])}
              <X 
                className="w-3 h-3 cursor-pointer" 
                onClick={() => {
                  const newFilters = {
                    ...filters,
                    amountRange: [0, maxAmount] as [number, number]
                  };
                  onChange(newFilters);
                }}
              />
            </Badge>
          )}
          {(filters.dateRange.start || filters.dateRange.end) && (
            <Badge variant="secondary" className="gap-1">
              Date: {filters.dateRange.start || 'Any'} to {filters.dateRange.end || 'Any'}
              <X 
                className="w-3 h-3 cursor-pointer" 
                onClick={() => {
                  const newFilters = {
                    ...filters,
                    dateRange: {}
                  };
                  onChange(newFilters);
                }}
              />
            </Badge>
          )}
        </div>
      )}
    </div>
  );
}